import { iconsPath } from '@shared/css.const';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import { USER_ROLE } from '@modules/identity/api/const';
import { UserRolesType } from '@modules/core/api/types';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_EVENTS } from '@shared/analytics/events';
import { ClientsRoute } from '@modules/clients/presentation/routes';
import { PayoutsRoute, TutorialsRoute } from '@modules/core/presentation/v2/routes';
import { BankApplicationsKanbanRoute, CasesRoute } from '@modules/opportunities/presentation/routes';
import {
  Bank, BarChart, Briefcase, FileSignature, FileSpreadsheet, FileStack, Info, Payments, User, Users
} from '@huspy/briks-icons';
import { DashboardRoute } from '@modules/dashboard/presentation/routes';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import {
  BankProductsRoute, BankUpdatesRoute, DocumentsCenterRoute
} from '@modules/banks/presentation/v2/routes';
import { ProposalsListRoute } from '@modules/proposals/presentation/routes';
import { ReferAndEarnListRoute } from '@modules/refer-and-earn/presentation/routes';

type SidebarLinks = {
  title?: string;
  links: {
    label: string;
    id: string;
    icon?: string;
    svgIcon?: React.ReactNode;
    roles?: UserRolesType[];
    to: string;
    onClick?(): void;
    featureActive: boolean;
  }[];
}[];

const handleClientHubLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.CLIENT_HUB_SIDEBAR_ICON_CLICK);
};

const handleCasesSidebarLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.CASES_SIDEBAR_ICON_CLICK);
};

const handleDashboardLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.DASHBOARD_SIDEBAR_ICON_CLICK);
};

const handleClickProposalsHubLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.PROPOSAL.SIDEBAR_CLICKED);
};

const handlePayoutsSidebarLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.PAYOUTS_SIDEBAR_CLICKED);
};

const handleDocumentCenterSidebarLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.DOCUMENT_CENTER_SIDEBAR_CLICKED);
};

const handleBankUpdatesSidebarLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.BANK_UPDATES_SIDEBAR_CLICKED);
};

const handleTutorialsSidebarLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.MISCELLANEOUS.TUTORIALS_SIDEBAR_CLICKED);
};

const handleReferAndEarnSidebarLinkClick = () => {
  trackAmplitudeEvent(USER_EVENTS.REFER_AND_EARN.SIDEBAR_CLICKED);
};

const useGetSidebarLinks = () => {
  const { featureFlags, isFeatureActive } = useFeatureFlags();
  const { user } = usePersistUser();
  const { t } = useTranslation();

  // eslint-disable-next-line no-underscore-dangle
  const _sidebarLinks: SidebarLinks = useMemo(() => [
    {
      links: [
        {
          label: t('common.layout.tabs.dashboard'),
          icon: `${iconsPath}/dashboard.svg`,
          to: DashboardRoute.to,
          id: 'dashboard-menu',
          svgIcon: <BarChart color='#6B7280' width={ 20 } height={ 20 } />,
          roles: [USER_ROLE.manager],
          onClick: handleDashboardLinkClick,
          featureActive: isFeatureActive('dashboards.module'),
        }
      ],
    },
    {
      title: t('common.layout.tabs.workspace.title'),
      links: [
        {
          label: t('clients.v2.title'),
          icon: `${iconsPath}/clients.svg`,
          id: 'client-hub-menu',
          onClick: handleClientHubLinkClick,
          svgIcon: <User color='#6B7280' width={ 20 } height={ 20 } />,
          to: ClientsRoute.to,
          featureActive: isFeatureActive('clients.module'),
        },
        {
          label: t('proposals.title'),
          icon: `${iconsPath}/proposals.svg`,
          id: 'proposals-menu',
          svgIcon: <FileSpreadsheet color='#6B7280' width={ 20 } height={ 20 } />,
          to: ProposalsListRoute.to,
          onClick: handleClickProposalsHubLinkClick,
          featureActive: isFeatureActive('proposals.module'),
        },
        {
          label: t('common.layout.tabs.workspace.cases'),
          icon: `${iconsPath}/cases.svg`,
          id: 'cases-menu',
          svgIcon: <FileSignature color='#6B7280' width={ 20 } height={ 20 } />,
          onClick: handleCasesSidebarLinkClick,
          to: IS_SPAIN_ENV ? BankApplicationsKanbanRoute.to : CasesRoute.to,
          featureActive: isFeatureActive('opportunities.module'),
        }
      ],
    },
    {
      title: t('common.layout.tabs.tools.title'),
      links: [
        {
          label: t('common.layout.tabs.tools.bankUpdates'),
          to: BankUpdatesRoute.to,
          svgIcon: <Briefcase color='#6B7280' width={ 20 } height={ 20 } />,
          onClick: handleBankUpdatesSidebarLinkClick,
          id: 'bank-updates-menu',
          featureActive: isFeatureActive('banks.updates'),
        },
        {
          label: t('common.layout.tabs.tools.bankProducts'),
          to: BankProductsRoute.to,
          svgIcon: <Bank color='#6B7280' width={ 20 } height={ 20 } />,
          id: 'bank-products-menu',
          featureActive: isFeatureActive('banks.products'),
        },
        {
          label: t('common.layout.tabs.tools.documentsCenter'),
          to: DocumentsCenterRoute.to,
          svgIcon: <FileStack color='#6B7280' width={ 20 } height={ 20 } />,
          onClick: handleDocumentCenterSidebarLinkClick,
          id: 'documents-center-menu',
          featureActive: isFeatureActive('banks.documents'),
        },
        {
          label: t('common.layout.tabs.tools.payouts'),
          to: PayoutsRoute.to,
          svgIcon: <Payments color='#6B7280' width={ 20 } height={ 20 } />,
          onClick: handlePayoutsSidebarLinkClick,
          roles: [USER_ROLE.manager],
          id: 'payouts-menu',
          featureActive: isFeatureActive('payouts.module'),
        },
        {
          label: t('referAndEarn.navLinkText'),
          to: ReferAndEarnListRoute.to,
          svgIcon: <Users color='#6B7280' width={ 20 } height={ 20 } />,
          onClick: handleReferAndEarnSidebarLinkClick,
          id: 'refer-and-earn-menu',
          featureActive: isFeatureActive('referAndEarn.module'),
        },
        {
          label: t('common.layout.tabs.tools.tutorials'),
          to: TutorialsRoute.to,
          svgIcon: <Info color='#6B7280' width={ 20 } height={ 20 } />,
          onClick: handleTutorialsSidebarLinkClick,
          id: 'tutorials-menu',
          featureActive: isFeatureActive('tutorials.module'),
        }
      ],
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [featureFlags, isFeatureActive, t]);

  const sidebarLinks = useMemo(() => _sidebarLinks
    .map((section) => ({
      ...section,
      links: section.links
        .filter(((link) => !link?.roles?.length || link.roles.some((role) => user?.roles?.includes(role))))
        .filter(((link) => link.featureActive === undefined || link.featureActive)),
    }))
    .filter((section) => section.links.length > 0), [_sidebarLinks, user?.roles]);

  return { sidebarLinks };
};

export default useGetSidebarLinks;
